import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { reloadPage } from '../consent/reloadPage'
import {
  clearChannelMarkupV2RefreshCookie,
  getChannelMarkupV2RefreshCookie,
  setChannelMarkupV2RefreshCookie,
} from 'lib/channelMarkup/channelMarkupV2RefreshCookie'
import { getChannelMarkupV2RefreshKey, getIsChannelMarkupV2PricedAsLiveUser } from 'selectors/channelMarkupSelector'
import { useAppSelector } from './reduxHooks'

/**
 * Purchase confirmation pages fire one-off purchase tracking that a reload would repeat, and their
 * prices are already settled. Joining LuxPlus+ changes the tier right there, so the reload waits
 * until the user leaves the page. Matches the routes in `PageRoutes.tsx`:
 * `/booking/confirmation`, `/booking/guest-confirmation`, `/bb/booking/confirmation` and
 * `/gift-cards/confirmation`.
 */
const purchaseConfirmationUrlPattern = /^\/[a-z]{2}\/((bb\/)?booking\/(guest-)?confirmation|gift-cards\/confirmation)(\/|$)/

/**
 * Channel markup v2 prices follow who the user is now. When the live account would price
 * differently from the user this page was priced as (they signed in, logged out, or joined
 * LuxPlus+), this reloads the page so the server prices them as they are now.
 *
 * It reloads at most once per pricing, so a server render that cannot see the change yet (and
 * prices the old user again) never loops. The guard is cleared once the page is priced as the
 * live user, so a later change to that same pricing can reload again. It checks again on every
 * navigation, so another tab that skipped its reload (while this one held the guard) reloads on
 * its next page.
 */
export default function useChannelMarkupV2PriceRefresh() {
  const refreshKey = useAppSelector(getChannelMarkupV2RefreshKey)
  const isPricedAsLiveUser = useAppSelector(getIsChannelMarkupV2PricedAsLiveUser)
  const { pathname } = useLocation()

  useEffect(() => {
    const reloadedKey = getChannelMarkupV2RefreshCookie()
    if (!refreshKey) {
      if (reloadedKey && isPricedAsLiveUser) {
        clearChannelMarkupV2RefreshCookie()
      }
      return
    }
    if (reloadedKey === refreshKey || purchaseConfirmationUrlPattern.test(pathname)) {
      return
    }
    setChannelMarkupV2RefreshCookie(refreshKey)
    reloadPage()
  }, [refreshKey, isPricedAsLiveUser, pathname])
}
